import React, { useEffect, useState } from "react";
import chainList from "../config/chainList";
import useTokenQuery from "../hooks/useTokenQuery";
import {
  DEFAULT_CHAIN_ID,
  TEST_CHAIN_ID,
} from "../config/constants";
import useCrossChainList from "../hooks/useCrossChainList";
import { isNativeToken, isTokenCompatible, sortTokenList } from "../utils/tokenUtils";
import useChainGasPrice, { IGasPriceOption } from "../hooks/useChainGasPrice";
import { graphql, useStaticQuery } from "gatsby";
import { useCustomTokens } from "../hooks/useCustomTokens";

export interface Chain {
  chainId: number
  chainName: string
  isTestChain: boolean
  isLocalChain: boolean
  multicallAddress: string
  multicall2Address?: string
  rpcUrl?: string
  blockExplorerUrl?: string
  nativeCurrency?: {
    name: string
    symbol: string
    decimals: number
  }
  getExplorerAddressLink: (address: string) => string
  getExplorerTransactionLink: (address: string) => string
}

export interface IChain {
  chainId: number;
  chainKey: string;
  name: string;
  rpcUrl: string;
  multicall?: string;
  currency: string;
  weth: string;
  nativeTokenReserve: number;
  usd?: string;
  dexScreenerChainId: string;
  dexToolsChainId: string;
  image?: string;
  useDappChain?: Chain;
  explorerUrl: string | undefined;
  maxChunkSize?: number;
}

export interface IToken {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  icon: string;
}

export interface IChainConfigContext {
  supportedChains: IChain[];
  supportedCrossChains: IChain[];
  supportedTokens: IToken[][] | undefined;
  getChainById: (chainId: number) => IChain | undefined;
  getChainNameById: (chainId: number) => string | undefined;
  isChainIdSupported: (chainId: number, crossChain?: boolean) => boolean;
  getTokensByChainId: (chainId: number) => Promise<IToken[]>;
  getTokenByAddress: (
    tokenAddress: string,
    chainId: number,
  ) => IToken | undefined;
  getTokenByTicker: (
    ticker: string,
    chainId: number,
  ) => IToken | undefined;
  getBridgeSupportedTokens: (
    chainIdFrom: number,
    chainIdTo: number,
  ) => IToken[] | undefined;
  addCustomToken: (chainId: number, token: IToken) => void;
  removeCustomToken: (chainId: number, tokenAddress: string) => void;
  isCustomToken: (chainId: number, tokenAddress: string) => boolean;
}

const ChainConfigContext = React.createContext<IChainConfigContext>(
  {} as IChainConfigContext,
);

const ChainConfigProvider = ({ children }) => {
  const { getTokenList } = useTokenQuery();
  const { getCrossChainList } = useCrossChainList();
  const { 
    customTokens, 
    addCustomToken: addCustomTokenToStorage, 
    removeCustomToken: removeCustomTokenFromStorage,
    isCustomToken,
    getCustomTokensForChain 
  } = useCustomTokens();

  const { allTokens } = useStaticQuery(graphql`
    query {
      allTokens {
        chains {
          chainId
          tokens {
            address
            decimals
            icon
            name
            symbol
            usd
          }
        }
      }
    }
  `);

  
  const enabledChainIds = allTokens?.chains?.map(chain => chain.chainId) || [];
  
  const [supportedCrossChains, setSupportedCrossChains] = useState<IChain[]>([]);
  const [supportedChains, setSupportedChains] = useState<IChain[]>(
    chainList.filter(chain => enabledChainIds.includes(chain.chainId))
  );

  const mergeTokensWithCustom = (chainId: number, staticTokens: IToken[]): IToken[] => {
    const customTokensForChain = getCustomTokensForChain(chainId);
    const allTokens = [...staticTokens];
    
    customTokensForChain.forEach(customToken => {
      const exists = allTokens.some(
        token => token.address.toLowerCase() === customToken.address.toLowerCase()
      );
      if (!exists) {
        allTokens.push(customToken);
      }
    });

    const nativeTokens = allTokens.filter(token => isNativeToken(token.address));
    const nonNativeTokens = allTokens.filter(token => !isNativeToken(token.address));
    
    return nativeTokens.length > 0 
      ? [...nativeTokens.slice(0, 1), ...nonNativeTokens]
      : nonNativeTokens;
  };

  const [supportedTokens, setSupportedTokens] = useState<IToken[][] | undefined>(
    allTokens?.chains?.reduce((acc, chain) => {
      const tokens = chain.tokens;
      const mergedTokens = mergeTokensWithCustom(chain.chainId, tokens);
      acc[chain.chainId] = mergedTokens;
      return acc;
    }, {})
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (allTokens?.chains && Object.keys(customTokens).length >= 0) {
      const newSupportedTokens = allTokens.chains.reduce((acc, chain) => {
        const tokens = chain.tokens;
        const mergedTokens = mergeTokensWithCustom(chain.chainId, tokens);
        acc[chain.chainId] = mergedTokens;
        return acc;
      }, {});
      
      // Also update any chains that have custom tokens but no static tokens
      Object.keys(customTokens).forEach(chainIdStr => {
        const chainId = parseInt(chainIdStr);
        if (!newSupportedTokens[chainId]) {
          newSupportedTokens[chainId] = mergeTokensWithCustom(chainId, []);
        }
      });
      
      setSupportedTokens(newSupportedTokens);
    }
  }, [customTokens, allTokens]);

  const getTokensByChainId = async (chainId: number) => {
    let _tokens: IToken[] = [];
    _tokens = supportedTokens?.[chainId]?.length
      ? supportedTokens?.[chainId]
      : ((await getTokenList(chainId)) as unknown as IToken[]);

    const mergedTokens = mergeTokensWithCustom(chainId, _tokens);
    _tokens = sortTokenList(mergedTokens);
    
    let _supportedTokens = supportedTokens || [];
    _supportedTokens[chainId] = _tokens;
    setSupportedTokens(_supportedTokens);
    return _tokens;
  };

  const getTokenByAddress = (
    tokenAddress: string,
    chainId: number,
  ): IToken | undefined => {
    if (!!supportedTokens?.[chainId]?.length) {
      if (isNativeToken(tokenAddress)) {
        return supportedTokens?.[chainId]?.find(
          (e) => isNativeToken(e.address),
        );
      } else {
        return supportedTokens?.[chainId]?.find(
          (e) => e.address?.toLowerCase() == tokenAddress?.toLowerCase(),
        );
      }
    } else {
      if (!loading) {
        setLoading(true);
        getTokensByChainId(chainId).finally(() => {
          setLoading(false);
        });
      }
    }
  };

  const getTokenByTicker = (
    ticker: string,
    chainId: number,
  ): IToken | undefined => {
    if (!!supportedTokens?.[chainId]?.length) {
      return supportedTokens?.[chainId]?.find(
        (e) => ticker === "" 
          ? isNativeToken(e.address)
          : e.symbol?.toLowerCase() === ticker?.toLowerCase(),
      );
    } else {
      if (!loading) {
        setLoading(true);
        getTokensByChainId(chainId).finally(() => {
          setLoading(false);
        });
      }
    }
  };

  const getChainById = (chainId): IChain | undefined => {
    chainId = parseInt(`${chainId}`);
    return supportedChains.find((network) => network.chainId === chainId);
  };

  const getChainNameById = (chainId) => {
    return supportedChains.find(
      (network) => `${network.chainId}` === `${chainId}`,
    )?.name;
  };

  const isChainIdSupported = (chainId, crossChain = false) => {
    return !!(crossChain ? supportedCrossChains : supportedChains).find(
      (network) => `${network.chainId}` === `${chainId}`,
    )?.chainId;
  };

  const getBridgeSupportedTokens = (chainIdFrom: number, chainIdTo: number) => {
    if (!supportedTokens?.[chainIdFrom]?.length && !loading) {
      getTokensByChainId(chainIdFrom);
      return undefined;
    }
    if (!supportedTokens?.[chainIdTo]?.length && !loading) {
      getTokensByChainId(chainIdTo);
      return undefined;
    }
    
    let tokensFrom = supportedTokens?.[chainIdFrom];
    let tokensTo = supportedTokens?.[chainIdTo];
    
    tokensFrom = tokensFrom?.filter((tokenFrom) =>
      tokensTo?.some((tokenTo) => isTokenCompatible(tokenFrom, tokenTo))
    );
    
    return tokensFrom;
  };

  const addCustomToken = (chainId: number, token: IToken) => {
    addCustomTokenToStorage(chainId, token);
  };

  const removeCustomToken = (chainId: number, tokenAddress: string) => {
    removeCustomTokenFromStorage(chainId, tokenAddress);
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await getTokensByChainId(DEFAULT_CHAIN_ID);
      setLoading(false);
    };

    initialize();

    getCrossChainList().then((chains: [{ chainId: number }]) => {
      const crossChainIds = chains.map((e) => e.chainId);
      setSupportedCrossChains(
        chainList.filter(
          (e) => 
            (crossChainIds.includes(e.chainId) || e.chainId === TEST_CHAIN_ID) && 
            enabledChainIds.includes(e.chainId)
        ),
      );
    });
  }, []);

  useEffect(() => {
    if (enabledChainIds?.length && supportedCrossChains?.length) {
      setSupportedCrossChains(
        supportedCrossChains.filter(
          (e) => enabledChainIds.includes(e.chainId)
        )
      );
    }
  }, [enabledChainIds.length, supportedCrossChains.length]);

  return (
    <ChainConfigContext.Provider
      value={{
        supportedChains,
        supportedCrossChains,
        supportedTokens,
        getChainById,
        getChainNameById,
        getTokensByChainId,
        getTokenByAddress,
        getTokenByTicker,
        isChainIdSupported,
        getBridgeSupportedTokens,
        addCustomToken,
        removeCustomToken,
        isCustomToken,
      }}
    >
      {children}
    </ChainConfigContext.Provider>
  );
};

export { ChainConfigContext, ChainConfigProvider };
